"use client";

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertCircle,
  CalendarCheck,
  CheckCircle2,
  Gauge,
} from "lucide-react";
import API_URL from "../../../src/Config";
import "./allSection.css";

const INITIAL_FILTERS = {
  cycle_year: "",
  start_date: "",
  end_date: "",
};

const INITIAL_SUMMARY = {
  compliance_score: {
    value: null,
    label: "",
    change_vs_previous_cycle: null,
    trend: "steady",
  },
  open_non_conformances: { count: 0, label: "" },
  cycle_completion: {
    value: null,
    answered: 0,
    expected: 0,
    not_yet_started: 0,
  },
  active_cycle: null,
};

const INITIAL_CHARTS = {
  rating_mix: [],
  weakest_sections: [],
  cycle_trend: [],
};

const RATING_COLORS = {
  FM: "hsl(142, 71%, 45%)",
  PM: "hsl(38, 92%, 50%)",
  NC: "hsl(0, 84%, 60%)",
  NYS: "hsl(217, 91%, 60%)",
  NA: "hsl(215, 16%, 47%)",
};

const numberOrZero = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatValue = (value, suffix = "") => {
  if (value === null || value === undefined || value === "") return "NA";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return `${numericValue}${suffix}`;
};

const formatDate = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatScoreChange = (value) => {
  if (value === null || value === undefined || value === "") return "No previous cycle";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "No previous cycle";
  const prefix = numericValue > 0 ? "+" : "";
  return `${prefix}${numericValue.toFixed(1)}% vs previous cycle`;
};

const formatTrendLabel = (trend) => {
  if (!trend) return "Steady";
  return trend.charAt(0).toUpperCase() + trend.slice(1);
};

const buildErrorMessage = (filters) => {
  if ((filters.start_date && !filters.end_date) || (!filters.start_date && filters.end_date)) {
    return "Select both start and end dates.";
  }
  if (
    filters.start_date &&
    filters.end_date &&
    new Date(filters.end_date) < new Date(filters.start_date)
  ) {
    return "End date cannot be before start date.";
  }
  return "";
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="newdashboard-tooltip">
      <p className="newdashboard-tooltip-label">
        {payload[0]?.payload?.label || label}
      </p>
      {payload.map((entry) => (
        <p key={entry.dataKey || entry.name} style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
      {payload[0]?.payload?.countsTowardsScore !== undefined && (
        <p>
          Score Impact:{" "}
          {payload[0].payload.countsTowardsScore ? "Counts" : "Excluded"}
        </p>
      )}
      {payload[0]?.payload?.sectionTitle && (
        <p>{payload[0].payload.sectionTitle}</p>
      )}
      {payload[0]?.payload?.assessed !== undefined && (
        <p>Assessed: {payload[0].payload.assessed}</p>
      )}
      {payload[0]?.payload?.answered !== undefined && (
        <p>Answered: {payload[0].payload.answered}</p>
      )}
      {payload[0]?.payload?.ended && <p>Ended: {payload[0].payload.ended}</p>}
    </div>
  );
};

export function QCIndexSection() {
  const [summary, setSummary] = useState(INITIAL_SUMMARY);
  const [charts, setCharts] = useState(INITIAL_CHARTS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchAvailableYears = useCallback(async () => {
    try {
      setFilterLoading(true);
      const authKey = localStorage.getItem("authKey") || "";
      const response = await axios.get(
        `${API_URL}/api/qc/reports/available-years`,
        { headers: { Authorization: authKey } }
      );
      const years = Array.isArray(response.data?.available_years)
        ? response.data.available_years
        : [];
      setAvailableYears(years);
    } catch (error) {
      console.error("Error fetching QC years:", error);
    } finally {
      setFilterLoading(false);
    }
  }, []);

  const fetchQCData = useCallback(async (currentFilters = INITIAL_FILTERS) => {
    const validationMessage = buildErrorMessage(currentFilters);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const authKey = localStorage.getItem("authKey") || "";
      const params = new URLSearchParams();

      if (currentFilters.cycle_year) {
        params.append("cycle_year", currentFilters.cycle_year);
      }
      if (currentFilters.start_date && currentFilters.end_date) {
        params.append("start_date", currentFilters.start_date);
        params.append("end_date", currentFilters.end_date);
      }

      const response = await axios.get(
        `${API_URL}/api/kpi/qc-index?${params.toString()}`,
        { headers: { Authorization: authKey } }
      );

      setSummary({ ...INITIAL_SUMMARY, ...(response.data?.summary || {}) });
      setCharts({ ...INITIAL_CHARTS, ...(response.data?.charts || {}) });
    } catch (error) {
      console.error("Error fetching QC index KPIs:", error);
      setErrorMessage(
        error?.response?.data?.message || "Failed to load QC index KPIs."
      );
      setSummary(INITIAL_SUMMARY);
      setCharts(INITIAL_CHARTS);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filters, [key]: value };

    if (key === "start_date" && !value) {
      nextFilters.end_date = "";
    }

    setFilters(nextFilters);
    const validationMessage = buildErrorMessage(nextFilters);
    setErrorMessage(validationMessage);

    if (!validationMessage) {
      fetchQCData(nextFilters);
    }
  };

  const handleResetFilters = () => {
    const resetFilters = INITIAL_FILTERS;
    setFilters(resetFilters);
    fetchQCData(resetFilters);
  };

  useEffect(() => {
    fetchAvailableYears();
  }, [fetchAvailableYears]);

  useEffect(() => {
    fetchQCData(INITIAL_FILTERS);
  }, [fetchQCData]);

  const ratingMixData = (charts.rating_mix || []).map((item) => ({
    rating: item.rating,
    label: `${item.rating} - ${item.label || item.rating}`,
    count: numberOrZero(item.count),
    countsTowardsScore: Boolean(item.counts_towards_score),
  }));

  const weakestSectionsData = (charts.weakest_sections || []).map((item) => ({
    section: item.section_code || item.section || "NA",
    sectionTitle: item.section || "NA",
    assessed: numberOrZero(item.assessed),
    "Not Complied": numberOrZero(item.not_complied),
    "Partially Met": numberOrZero(item.partially_met),
    "Section Score": numberOrZero(item.score),
  }));

  const trendData = (charts.cycle_trend || []).map((item) => ({
    cycle: item.cycle_year,
    ended: formatDate(item.ended),
    answered: numberOrZero(item.answered),
    "Compliance Score": numberOrZero(item.score),
    "Not Complied": numberOrZero(item.not_complied),
  }));

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="newdashboard-index-section">
      <div className="newdashboard-index-header">
        <div>
          <h2 className="newdashboard-section-title">QC Index</h2>
          <p className="newdashboard-index-subtitle">
            Track inspection compliance, non-conformance, and cycle progress
          </p>
        </div>

        <div className="newdashboard-index-filters">
          <div className="newdashboard-filter-group">
            <label className="newdashboard-filter-label">Cycle Year</label>
            <select
              className="newdashboard-select"
              value={filters.cycle_year}
              onChange={(event) =>
                handleFilterChange("cycle_year", event.target.value)
              }
              disabled={filterLoading}
            >
              <option value="">Latest Cycle</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="newdashboard-filter-group">
            <label className="newdashboard-filter-label">Start Date</label>
            <input
              className="newdashboard-date-input"
              type="date"
              value={filters.start_date}
              onChange={(event) =>
                handleFilterChange("start_date", event.target.value)
              }
            />
          </div>

          <div className="newdashboard-filter-group">
            <label className="newdashboard-filter-label">End Date</label>
            <span
              className="newdashboard-index-tooltip-wrap"
              title={!filters.start_date ? "Select start date first" : ""}
            >
              <input
                className="newdashboard-date-input"
                type="date"
                value={filters.end_date}
                min={filters.start_date || undefined}
                disabled={!filters.start_date}
                onChange={(event) =>
                  handleFilterChange("end_date", event.target.value)
                }
              />
            </span>
          </div>

          <div className="newdashboard-filter-actions">
            <button
              className="newdashboard-btn newdashboard-btn-outline"
              type="button"
              onClick={handleResetFilters}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="newdashboard-index-error">{errorMessage}</div>
      )}

      <div className="newdashboard-kpi-grid">
        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Compliance Score</h3>
            <Gauge className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value newdashboard-text-success">
              {formatValue(summary.compliance_score?.value, "%")}
            </div>
            <p className="newdashboard-card-subtitle">
              {formatTrendLabel(summary.compliance_score?.trend)}.{" "}
              {formatScoreChange(
                summary.compliance_score?.change_vs_previous_cycle
              )}
            </p>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Open Non-Conformances</h3>
            <AlertCircle className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value newdashboard-text-danger">
              {summary.open_non_conformances?.count || 0}
            </div>
            <p className="newdashboard-card-subtitle">
              {summary.open_non_conformances?.label || "Items not complied"}
            </p>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Cycle Completion</h3>
            <CheckCircle2 className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value newdashboard-text-primary">
              {formatValue(summary.cycle_completion?.value, "%")}
            </div>
            <p className="newdashboard-card-subtitle">
              {summary.cycle_completion?.answered || 0} of{" "}
              {summary.cycle_completion?.expected || 0} assessed,{" "}
              {summary.cycle_completion?.not_yet_started || 0} not yet started
            </p>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Active Cycle</h3>
            <CalendarCheck className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value-medium">
              {summary.active_cycle?.cycle_year || "NA"}
            </div>
            <p className="newdashboard-card-subtitle">
              {formatDate(summary.active_cycle?.starts)} to{" "}
              {formatDate(summary.active_cycle?.ends)}.{" "}
              {summary.active_cycle?.days_remaining ?? "NA"} days remaining
            </p>
          </div>
        </div>
      </div>

      <div className="newdashboard-charts-grid">
        <div className="newdashboard-card">
          <div className="newdashboard-card-header-full">
            <h3 className="newdashboard-card-title">Rating Mix</h3>
            <p className="newdashboard-card-description">
              Fully met, partially met, non-compliant, and excluded ratings
            </p>
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-chart-container">
              {ratingMixData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Pie
                      data={ratingMixData}
                      dataKey="count"
                      nameKey="rating"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                    >
                      {ratingMixData.map((entry) => (
                        <Cell
                          key={entry.rating}
                          fill={RATING_COLORS[entry.rating] || "#6b7280"}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </div>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header-full">
            <h3 className="newdashboard-card-title">Weakest Sections</h3>
            <p className="newdashboard-card-description">
              Sections with not complied and partially met findings
            </p>
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-chart-container">
              {weakestSectionsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={weakestSectionsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="section" stroke="#6b7280" fontSize={12} />
                    <YAxis yAxisId="count" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      yAxisId="score"
                      orientation="right"
                      stroke="#6b7280"
                      fontSize={12}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      yAxisId="count"
                      dataKey="Not Complied"
                      fill="hsl(0, 84%, 60%)"
                    />
                    <Bar
                      yAxisId="count"
                      dataKey="Partially Met"
                      fill="hsl(38, 92%, 50%)"
                    />
                    <Line
                      yAxisId="score"
                      type="monotone"
                      dataKey="Section Score"
                      stroke="hsl(217, 91%, 60%)"
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="newdashboard-card">
        <div className="newdashboard-card-header-full">
          <h3 className="newdashboard-card-title">Cycle Trend</h3>
          <p className="newdashboard-card-description">
            Compliance score and non-conformance trend across cycles
          </p>
        </div>
        <div className="newdashboard-card-content">
          <div className="newdashboard-chart-container">
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="cycle" stroke="#6b7280" fontSize={12} />
                  <YAxis yAxisId="count" stroke="#6b7280" fontSize={12} />
                  <YAxis
                    yAxisId="score"
                    orientation="right"
                    stroke="#6b7280"
                    fontSize={12}
                    domain={[0, 100]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    yAxisId="count"
                    dataKey="answered"
                    name="Answered"
                    fill="hsl(217, 91%, 60%)"
                  />
                  <Bar
                    yAxisId="count"
                    dataKey="Not Complied"
                    fill="hsl(0, 84%, 60%)"
                  />
                  <Line
                    yAxisId="score"
                    type="monotone"
                    dataKey="Compliance Score"
                    stroke="hsl(142, 71%, 45%)"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
